import React, { useEffect, useState } from 'react';
import { fetchSecurityImage } from '@common/services/featherIO';
import { ActivityIndicator } from 'react-native';

/**
 * Resolve any image reference into a short-lived (5-minute) pre-signed URL.
 * Accepts either an S3 keyPath ("workspaces/.../file.jpeg") or a full S3 URL
 * ("https://s3.<region>.amazonaws.com/<bucket>/<keyPath>?..."). Non-S3 sources
 * are returned unchanged by `fetchSecurityImage`.
 */
export const useImageSecurity = (src: string | null | undefined) => {
  const [imageUrl, setImageUrl] = useState<string>(src || '');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    if(typeof src !== 'string') {
      setLoading(false);
      return;
    }

    if (!src) {
      setImageUrl('');
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchSecurityImage(src)
      .then((url) => {
        if (cancelled) return;
        setImageUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setImageUrl(src);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return { imageUrl, loading };
};

type Props = {
  src: string | null | undefined;
  children: React.FC<{ imageUrl: string }>;
};
export const ImageSecurity: React.FC<Props> = ({ src, children }) => {
  const { imageUrl, loading } = useImageSecurity(src);

  return loading ? (
    <ActivityIndicator size="small" color="#0000ff" />
  ) : (
    children({ imageUrl })
  );
};
